/**
 * BAT 配置 API 客户端
 * 与 bsp_back 的 /api/v1/plugins/ 端点通信
 */

import apiClient from '@/api/client';

export interface BatConfig {
  project: string;
  bench_name: string;
  vehicle_model?: string;
  mountain?: string;
  ota_env?: string;
  project_alias?: {
    soc?: string;
    mcu?: string;
  };
  kind_map?: Record<string, string>;
  pkg_rules?: Record<string, any>;
  rmp_rules?: Record<string, any>;
  mcu_code_rules?: {
    git_url?: string;
    base_branch?: string;
    commit?: string;
    base_branch_slave?: string;
    commit_slave?: string;
  };
  checks?: any;
  [key: string]: any;
}

/** 字段来源徽标：标识该配置项是由哪一级数据源推导得到的 */
export type BatConfigFieldSource =
  | 'bmp'              // BMP 台架信息
  | 'itt_config'       // bvp_itt_config BVP准入工程名映射
  | 'existing'         // 已有 BAT 配置（增量更新）
  | 'default'          // 系统默认值
  | 'rcc_derived'      // RCC/RMP 脚本推导
  | 'bsp_package'      // BSP 平台产物包分析
  | 'bcr_context'      // BCR 任务上下文
  | 'llm_suggested';   // LLM 建议（Phase 2）

/** 待确认项：规则推导结果中低置信度、需人工确认的字段 */
export interface BatConfigPendingConfirmation {
  path: string;
  suggested: any;
  reason: string;
  confidence?: number;
}

/** 一键生成请求 */
export interface BatConfigGeneratePayload {
  project: string;
  package_id?: number;
  vehicle_model?: string;
  bench_name?: string;
  ota_env?: string;
  script_map?: Record<string, string>;
  use_llm?: boolean;
}

/** 一键生成结果（来自 bvp_back /third/bcr/query action=generate_from_bcr） */
export interface BatConfigGenerateResult {
  mode: 'create' | 'update' | 'locked';
  locked: boolean;
  config: BatConfig;
  field_sources?: Record<string, BatConfigFieldSource>;
  pending_confirmations?: BatConfigPendingConfirmation[];
  notes?: string[];
  llm_used?: boolean;
  llm_degraded_reason?: string | null;
  resolved_ota_env?: string;
}

export interface RmpAnalysisResult {
  kind_map: Record<string, string>;  // 从 RCC 推导的 kind_map
  rmp_rules: Record<string, any>;
  pkg_rules: Record<string, any>;
  script_analysis: Array<any>;
  rcc_records: Array<any>;
  notes: string[];
  bsp_analysis_applied?: boolean;  // 标记是否应用了 BSP 包分析
}

/**
 * 获取项目的 BAT 配置
 */
export async function getBatConfig(project: string): Promise<BatConfig | null> {
  try {
    const response = await apiClient.get('plugins/bat-config/', {
      params: { project },
    });
    return response.data?.data || null;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null; // 配置不存在
    }
    throw error;
  }
}

/**
 * 保存 BAT 配置
 */
export async function saveBatConfig(project: string, content: BatConfig): Promise<BatConfig> {
  const response = await apiClient.post('plugins/bat-config/save/', {
    project,
    content,
  });
  return response.data?.data || content;
}

/**
 * 从 BCR 上下文一键生成 BAT 配置（Phase 1：纯规则推导，BVP 侧完成）
 *
 * 后端代理：bvp_back POST /third/bcr/query {action: generate_from_bcr}
 * 推导规则全部在 bvp_back 侧完成，本接口只做鉴权 + 透传。
 */
export async function generateBatConfigFromBcr(
  payload: BatConfigGeneratePayload
): Promise<BatConfigGenerateResult> {
  const response = await apiClient.post('plugins/bat-config/generate-from-bcr/', payload);
  return response.data?.data;
}

/**
 * 查询项目在 BVP 配置中心是否被锁定（锁定后禁止生成/保存 BAT 配置）
 *
 * 后端代理：bvp_back GET /api/operation/configuration/bat-config/lock
 * 查询失败时返回 false（放行，由保存侧 423 兜底），避免后端抖动阻塞 UI。
 */
export async function getBatConfigLock(project: string): Promise<boolean> {
  try {
    const response = await apiClient.get('plugins/bat-config/lock/', {
      params: { project },
    });
    return !!response.data?.data?.locked;
  } catch (error) {
    console.error('[getBatConfigLock] error:', error);
    return false;
  }
}

/**
 * 查询项目的历史产物类型
 */
export async function getArtifactKinds(project: string): Promise<string[]> {
  try {
    const response = await apiClient.post('plugins/bat-rmp-query/', {
      action: 'get_artifact_kinds',
      project,
    });
    return response.data?.data || [];
  } catch (error) {
    console.error('[getArtifactKinds] error:', error);
    return [];
  }
}

/**
 * 分析 RMP 部署脚本
 * @param ota_env - RMP 部署脚本别名（如 hnas_ct）
 * @param scriptMap - 用户选择的产物脚本映射，如 { bsw: 'BSW-hnas...', mcu: 'MCU-hnas...' }
 *                    若不提供，则分析整个 ota_env 下的所有脚本
 * @param project - 可选：BAT 项目名，用于从 BSP 平台获取实际产物包分析
 */
export async function analyzeRmp(
  ota_env: string,
  scriptMap?: Record<string, string>,
  project?: string
): Promise<RmpAnalysisResult> {
  const response = await apiClient.post('plugins/bat-rmp-query/', {
    action: 'analyze_rmp',
    ota_env,
    script_map: scriptMap || {},
    project: project || undefined,
  });
  return response.data?.data || {
    rmp_rules: {},
    pkg_rules: {},
    script_analysis: [],
    rcc_records: [],
    notes: [],
  };
}

/**
 * 列出指定 ota_env 下的所有 RMP 部署脚本
 */
export async function listRmpScripts(ota_env: string): Promise<Array<{
  conf_version: string;
  package_kinds: string[];
  ota_envs: string[];
}>> {
  try {
    const response = await apiClient.post('plugins/bat-rmp-query/', {
      action: 'list_rmp_scripts',
      ota_env,
    });
    return response.data?.data || [];
  } catch (error) {
    console.error('[listRmpScripts] error:', error);
    return [];
  }
}

/** RMP OTA Environment（ota_env）候选：由项目名/台架名与 RMP 记录关键词匹配推导 */
export interface OtaEnvCandidate {
  ota_env: string;
  kinds: string[];
  score: number;
}

/**
 * 根据项目名 / 台架名，从 RMP 自动发现候选 ota_env（RMP OTA Environment）
 *
 * 后端代理：bvp_back POST /third/bcr/query {action: discover_ota_env}
 * 命中为空时返回 []，由用户手动填写。
 */
export async function discoverOtaEnv(
  project: string,
  benchName?: string
): Promise<OtaEnvCandidate[]> {
  const response = await apiClient.post('plugins/bat-rmp-query/', {
    action: 'discover_ota_env',
    project,
    bench_name: benchName || '',
  });
  return response.data?.data || [];
}
